import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { AUDIT_ACTIONS, type AuditAction } from '@tekvoice/shared';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDateTime } from '@/lib/utils';

const ALL = '__all__';

/** SPEC §10 — SUPER_ADMIN filterable audit feed. */
export function AuditSettingsPage(): React.JSX.Element {
  const [action, setAction] = React.useState<string>(ALL);
  const [page, setPage] = React.useState(1);

  const audit = useQuery({
    queryKey: ['audit', action, page],
    queryFn: () =>
      api.audit.list({
        page,
        pageSize: 25,
        ...(action === ALL ? {} : { action: action as AuditAction }),
      }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Audit log</CardTitle>
        <CardDescription>Append-only. Settings changes record key paths only, never values.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <Select
            value={action}
            onValueChange={(value) => {
              setAction(value);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-64">
              <SelectValue placeholder="All actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All actions</SelectItem>
              {AUDIT_ACTIONS.map((value) => (
                <SelectItem key={value} value={value}>
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">
            {audit.data ? `${audit.data.total} entries` : ' '}
          </span>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Entity</TableHead>
              <TableHead>Metadata</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(audit.data?.items ?? []).map((item) => (
              <TableRow key={item.id}>
                <TableCell className="whitespace-nowrap text-muted-foreground">
                  {formatDateTime(item.createdAt)}
                </TableCell>
                <TableCell>{item.actorName ?? item.actorEmail ?? 'system'}</TableCell>
                <TableCell>
                  <Badge variant="secondary">{item.action}</Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {item.entityType}:{item.entityId.slice(0, 10)}
                </TableCell>
                <TableCell className="max-w-md truncate text-xs text-muted-foreground">
                  {JSON.stringify(item.metadata)}
                </TableCell>
              </TableRow>
            ))}
            {audit.isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>

        <div className="flex items-center justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!audit.data?.hasMore}
            onClick={() => setPage((current) => current + 1)}
          >
            Next
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
